import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Download, Eye, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";

export default function ModuleLanding({ moduleKey, item, onRefresh }) {
  const [brief, setBrief] = useState(item.brief || "");
  const [html, setHtml] = useState(item.html || "");
  const [busy, setBusy] = useState(item.html_status === "generating");
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);

  const startPolling = () => {
    clearInterval(poll.current);
    let tries = 0;
    poll.current = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/mod/${moduleKey}/items`);
        const it = (data || []).find((x) => x.id === item.id);
        if (it?.html_status === "ready" && it.html) {
          clearInterval(poll.current); setHtml(it.html); setBusy(false);
          toast.success("Landing generada con IA"); onRefresh?.();
        } else if (it?.html_status === "error") {
          clearInterval(poll.current); setBusy(false); toast.error("La IA no pudo generar la landing. Reintenta.");
        }
      } catch { /* keep polling */ }
      if (tries > 70) { clearInterval(poll.current); setBusy(false); toast.error("La generación está tardando. Reintenta en un momento."); }
    }, 3000);
  };

  const generate = async () => {
    if (!brief.trim()) return toast.error("Describe qué landing quieres");
    setBusy(true);
    try {
      await api.post(`/mod/${moduleKey}/items/${item.id}/generate`, { brief });
      toast.info("Generando tu landing con IA… puede tardar 1-3 min");
      startPolling();
    } catch (e) { setBusy(false); toast.error(e?.response?.data?.detail || "Error al generar"); }
  };

  const download = () => {
    const blob = new Blob([html], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(item.titulo || "landing").replace(/\s+/g, "-").toLowerCase()}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-3" data-testid="module-landing">
      <div className="flex items-center gap-2 overline text-navy/40"><Sparkles className="w-3.5 h-3.5" /> Constructor de landing con IA</div>
      <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} data-testid="landing-brief"
        placeholder="Describe tu landing: producto/servicio, público objetivo, propuesta de valor y objetivo (ej. captar registros para un webinar de ciberseguridad para PYMES)."
        className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm" />
      <div className="flex gap-2">
        <button onClick={generate} disabled={busy} className="btn btn-primary" data-testid="landing-generate-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : html ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
          {html ? "Regenerar" : "Generar con IA"}
        </button>
        {html && <button onClick={download} className="btn btn-ghost" data-testid="landing-download-btn"><Download className="w-4 h-4" /> Descargar HTML</button>}
        {html && <a href={`data:text/html;charset=utf-8,${encodeURIComponent(html)}`} target="_blank" rel="noreferrer" className="btn btn-ghost" data-testid="landing-open-btn"><Eye className="w-4 h-4" /> Abrir</a>}
      </div>
      {html && (
        <div className="rounded-lg overflow-hidden border border-navy/10" data-testid="landing-preview">
          <div className="bg-navy/5 px-3 py-1.5 text-[11px] text-navy/40">Vista previa</div>
          <iframe title="preview" srcDoc={html} className="w-full h-96 bg-white" sandbox="allow-scripts" />
        </div>
      )}
    </div>
  );
}
